/* eslint-disable no-param-reassign */
import { createSlice, createSelector } from '@reduxjs/toolkit'
import turfDistance from '@turf/distance'
import { point, featureCollection } from '@turf/helpers'
import turfBbox from '@turf/bbox'
import turfBuffer from '@turf/buffer'
import locations from '../locations'

export const DISTANCE_OPTIONS = [ 5, 10, 25 ]

const initialState = {
  distance : 25,
  query : '',
  selectedLocation : null,
  locations,
  userPosition : null,
}

export const locatorSlice = createSlice( {
  name : 'locator',
  initialState,
  reducers : {
    setDistance : ( state, action ) => {
      state.distance = action.payload
      state.selectedLocation = null
    },
    setQuery : ( state, action ) => {
      state.query = action.payload
    },
    setSelectedLocation : ( state, action ) => {
      state.selectedLocation = action.payload
    },
    setUserPosition : ( state, action ) => {
      state.userPosition = action.payload
      state.selectedLocation = null
    },
  },
} )

export const {
  setDistance,
  setQuery,
  setSelectedLocation,
  setUserPosition,
} = locatorSlice.actions

export const distanceSelector = ( state ) => state.locator.distance
export const querySelector = ( state ) => state.locator.query
export const selectedLocationSelector = ( state ) => state.locator.selectedLocation
export const locationsSelector = ( state ) => state.locator.locations
export const userPositionSelector = ( state ) => state.locator.userPosition

export const filteredLocationsSelector = createSelector(
  [ distanceSelector, locationsSelector, userPositionSelector ],
  ( distance, locations, userPosition ) => sortLocations(
    filterLocations( locations, userPosition, distance ), userPosition,
  ),
)

export const mapCenterOrBoundsSelector = createSelector(
  [ filteredLocationsSelector, selectedLocationSelector, userPositionSelector, distanceSelector ],
  ( locations, selectedLocation, userPosition, distance ) => {
    if ( selectedLocation ) {
      const p = point( [ selectedLocation.longitude, selectedLocation.latitude ] )
      const bounds = turfBuffer( p, 1, { units : 'miles' } )

      return {
        bounds : turfBbox( bounds ),
      }
    }

    if ( locations.length > 0 ) {
      if ( locations.length === 1 ) {
        const p = point( [ locations[0].longitude, locations[0].latitude ] )
        const bounds = turfBuffer( p, distance, { units : 'miles' } )

        return {
          bounds : turfBbox( bounds ),
        }
      }
      const points = []

      locations.forEach( ( location ) => {
        points.push( point( [ location.longitude, location.latitude ] ) )
      } )

      const fc = featureCollection( points )
      const [ minLon, minLat, maxLon, maxLat ] = turfBbox( fc )

      return {
        bounds : [ [ minLon, minLat ], [ maxLon, maxLat ] ],
      }
    }

    if ( userPosition && userPosition.latitude && userPosition.longitude ) {
      const p = point( [ userPosition.longitude, userPosition.latitude ] )
      const bounds = turfBuffer( p, distance, { units : 'miles' } )

      return {
        bounds : turfBbox( bounds ),
      }
    }

    const p = point( [ -76.6905365, 39.2848291 ] )
    const bounds = turfBuffer( p, distance, { units : 'miles' } )

    return {
      bounds : turfBbox( bounds ),
    }
  },
)

export default locatorSlice.reducer

function sortLocations( locations, userPosition ) {
  if ( userPosition == null || userPosition.latitude == null || userPosition.longitude == null ) {
    return locations
  }

  return locations.slice().sort( ( locA, locB ) => {
    const distToA = ( locA.longitude - userPosition.longitude ) ** 2
    + ( locA.latitude - userPosition.latitude ) ** 2

    const distToB = ( locB.longitude - userPosition.longitude ) ** 2
    + ( locB.latitude - userPosition.latitude ) ** 2

    return distToA > distToB ? 1 : -1
  } )
}

function filterLocations( locations, userPosition, distance ) {
  if ( userPosition
    && userPosition.latitude
    && userPosition.longitude
    && distance ) {
    return locations.filter( ( location ) => {
      const userPoint = point( [ userPosition.longitude, userPosition.latitude ] )
      const locationPoint = point( [ location.longitude, location.latitude ] )

      const fromUser = turfDistance( userPoint, locationPoint, {
        units : 'miles',
      } )

      return fromUser <= distance
    } )
  }

  return locations
}
